import React, { useEffect, useState } from 'react';
import './App.css';

import {
  Header,
  Footer,
  Hero,
  Specialisations,
  Process,
  BlogSection,
  ContactSection,
  AboutProfessional,
  WhyMe,
  ForWhom,
  BlogPage,
  BlogArticlePage,
} from './components';

import { ZwierzetaCywilHub } from './components/ZwierzetaCywilHub';
import { ZwierzetaCywilLanding } from './components/ZwierzetaCywilLanding';
import { SporyZBankamiHub } from './components/SporyZBankamiHub';
import { SporyBankiLanding, BankLandingKey } from './components/SporyBankiLanding';
import GlobalSEO from './components/GlobalSEO';
import { CookieConsentBanner } from './components/CookieConsentBanner';


import { SupportedLocale } from './lib/content';

type Route =
  | 'home'
  | 'about'
  | 'blog'
  | 'article'
  | 'zwierzetaCywilHub'
  | 'zwierzetaCywilLanding'
| 'sporyBankiHub'
| 'sporyBankiLanding';

const BASE_TITLES = {
  pl: 'Pieńkowski – Kancelaria Adwokacka',
  en: 'Pieńkowski – Law Office',
  es: 'Pieńkowski – Despacho de Abogados',
} as const;

const ROUTE_TITLES = {
  home: {
    pl: 'Strona główna',
    en: 'Home',
    es: 'Inicio',
  },
  about: {
    pl: 'O nas',
    en: 'About us',
    es: 'Sobre nosotros',
  },
  blog: {
    pl: 'Blog',
    en: 'Blog',
    es: 'Blog',
  },
  article: {
    pl: 'Artykuł',
    en: 'Article',
    es: 'Artículo',
  },
  zwierzetaCywilHub: {
    pl: 'Sprawy cywilne dotyczące zwierząt',
    en: 'Civil animal cases',
    es: 'Casos civiles sobre animales',
  },
  zwierzetaCywilLanding: {
    pl: 'Sprawy cywilne dotyczące zwierząt',
    en: 'Civil animal cases',
    es: 'Casos civiles sobre animales',
  },
sporyBankiHub: { pl: 'Spory z bankami', en: 'Bank disputes', es: 'Disputas con bancos' },
sporyBankiLanding: { pl: 'Kredyty frankowe (CHF)', en: 'CHF loans', es: 'Préstamos CHF' },
} as const;
function App() {
  /* ===========================
     LOCALE
     =========================== */
  const [locale, setLocale] = useState<SupportedLocale>(() => {
    try {
      const stored = window.localStorage.getItem('locale') as SupportedLocale | null;
      if (stored === 'pl' || stored === 'en' || stored === 'es') return stored;
    } catch {}
    return 'pl';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('locale', locale);
    } catch {}
  }, [locale]);
  /* ===========================
     ROUTE + ARTICLE
     =========================== */
  const [route, setRoute] = useState<Route>(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';

    if (path === '/prawa-zwierzat/cywil' || path === '/zwierzeta/cywil') return 'zwierzetaCywilHub';
    if (path.startsWith('/prawa-zwierzat/cywil/') || path.startsWith('/zwierzeta/cywil/')) return 'zwierzetaCywilLanding';
if (path === '/spory-kredytobiorcow-z-bankami') return 'sporyBankiHub';
    if (path === '/blog') return 'blog';
    if (path.startsWith('/blog/')) return 'article';
    if (path === '/o-mnie' || path === '/about-me' || path === '/sobre-mi') return 'about';
    return 'home';
  });

  const [articleSlug, setArticleSlug] = useState<string>(() => {
    const path = window.location.pathname.replace(/\/+$/, '');
    if (path.startsWith('/blog/')) return path.replace('/blog/', '').replace('.html', '');
    return '';
  });

  // slug do landingu cywil-zwierzęcego
  const [zwierzetaCywilSlug, setZwierzetaCywilSlug] = useState<string>(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const p1 = '/prawa-zwierzat/cywil/';
    const p2 = '/zwierzeta/cywil/';
    if (path.startsWith(p1)) return path.replace(p1, '');
    if (path.startsWith(p2)) return path.replace(p2, '');
    return '';
  });
type BankLandingKey = 'franki' | 'skd' | 'wibor';
const [sporyBankiSlug, setSporyBankiSlug] = useState<BankLandingKey>('franki');


  useEffect(() => {
  // 1) Funkcja, która zawsze mapuje aktualny URL → route + slugi
    // Funkcja, która zawsze mapuje aktualny URL → route + slugi
  const syncRouteFromPath = () => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';

    // --- ZWIERZĘTA CYWIL: hub + landingi ---
    if (path === '/prawa-zwierzat/cywil' || path === '/zwierzeta/cywil') {
      setRoute('zwierzetaCywilHub');
      setArticleSlug('');
      setZwierzetaCywilSlug('');
      return;
    }

    const p1 = '/prawa-zwierzat/cywil/';
    const p2 = '/zwierzeta/cywil/';
    if (path.startsWith(p1) || path.startsWith(p2)) {
      setRoute('zwierzetaCywilLanding');
      setArticleSlug('');
      setZwierzetaCywilSlug(path.startsWith(p1) ? path.replace(p1, '') : path.replace(p2, ''));
      return;
    }

    // --- SPORY Z BANKAMI: hub + landingi (franki / skd / wibor) ---
    const bankHub = '/spory-kredytobiorcow-z-bankami';
    const bankPrefix = '/spory-kredytobiorcow-z-bankami/';

    if (path === bankHub) {
      setRoute('sporyBankiHub');
      setArticleSlug('');
      setZwierzetaCywilSlug('');
      setSporyBankiSlug('franki'); // default, bo BankLandingKey nie dopuszcza pustego stringa
      return;
    }

    if (path.startsWith(bankPrefix)) {
      const tail = path.replace(bankPrefix, '').replace('.html', '');
      if (tail === 'franki' || tail === 'skd' || tail === 'wibor') {
        setRoute('sporyBankiLanding');
        setArticleSlug('');
        setZwierzetaCywilSlug('');
        setSporyBankiSlug(tail); // tail jest już zawężony do BankLandingKey
        return;
      }
    }

    // --- BLOG ---
    if (path === '/blog') {
      setRoute('blog');
      setArticleSlug('');
      setZwierzetaCywilSlug('');
      return;
    }

    if (path.startsWith('/blog/')) {
      setRoute('article');
      setArticleSlug(path.replace('/blog/', '').replace('.html', ''));
      setZwierzetaCywilSlug('');
      return;
    }

    // --- O NAS / ABOUT ---
    if (path === '/o-nas' || path === '/o-mnie' || path === '/about-me' || path === '/sobre-mi') {
      setRoute('about');
      setArticleSlug('');
      setZwierzetaCywilSlug('');
      return;
    }

    // --- DEFAULT ---
    setRoute('home');
    setArticleSlug('');
    setZwierzetaCywilSlug('');
  };

  const patchHistory = () => {
    const w = window as any;

    if (w.__historyPatched) return;
    w.__historyPatched = true;

   const rawPush = window.history.pushState.bind(window.history) as (...args: any[]) => any;
const rawReplace = window.history.replaceState.bind(window.history) as (...args: any[]) => any;

window.history.pushState = ((...args: any[]) => {
  const ret = rawPush(...args);
  window.dispatchEvent(new Event('locationchange'));
  return ret;
}) as any;

window.history.replaceState = ((...args: any[]) => {
  const ret = rawReplace(...args);
  window.dispatchEvent(new Event('locationchange'));
  return ret;
}) as any}; 


  patchHistory();

  // 3) Nasłuchujemy: back/forward + nasze własne zmiany URL
  window.addEventListener('popstate', syncRouteFromPath);
  window.addEventListener('locationchange', syncRouteFromPath);

  // 4) I odpalamy raz na start
  syncRouteFromPath();

  return () => {
    window.removeEventListener('popstate', syncRouteFromPath);
    window.removeEventListener('locationchange', syncRouteFromPath);
  };
}, []);

  /* ===========================
     ACTIVE SECTION
     =========================== */
  const [activeSection, setActiveSection] = useState<string>('hero');

  useEffect(() => {
    if (route !== 'home') {
      setActiveSection('');
      return;
    }

    const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id]'));
    const marker = window.innerHeight * 0.35;

    const onScroll = () => {
      let current = sections[0]?.id || 'hero';
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top - marker <= 0) current = section.id;
        else break;
      }
      setActiveSection(current);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [route]);

  /* ===========================
     POLITYKA PRYWATNOŚCI – MODAL
     =========================== */
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // ESC zamyka modal
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPrivacyOpen(false);
    };
    if (privacyOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [privacyOpen]);

  // event z formularza
  useEffect(() => {
    const handler = () => setPrivacyOpen(true);
    window.addEventListener('legal:privacy', handler);
    return () => window.removeEventListener('legal:privacy', handler);
  }, []);

  

  /* ===========================
     SET LOCALE FROM URL
     =========================== */
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/about-me') setLocale('en');
    else if (path === '/sobre-mi') setLocale('es');
    else if (path === '/o-mnie') setLocale('pl');
else if (path === '/o-nas') setLocale('pl');
  }, [route]);

  /* ===========================
     DOCUMENT TITLE (SEO)
     =========================== */
  useEffect(() => {
    const base = BASE_TITLES[locale];
    const routeTitle = ROUTE_TITLES[route]?.[locale];
// Blog: tytuł ustawiamy w Helmet (GlobalSEO / Blog meta), więc App nie nadpisuje
if (window.location.pathname.startsWith('/blog/')) return;

// home → tylko nazwa kancelarii
if (route === 'home') {
  document.title = base;
  return;
}

// pozostałe podstrony → "O nas | Nazwa kancelarii"
document.title = `${routeTitle} | ${base}`;
  }, [locale, route]);

  /* ===========================
     REVEAL
     =========================== */
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    requestAnimationFrame(() => {
      elements.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.9) el.classList.add('is-visible');
      });
    });

    return () => observer.disconnect();
  }, [route, locale]);

  /* ===========================
     HASH SCROLL
     =========================== */
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.replace('#', '');
    if (!id) return;

    const t1 = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);

    const t2 = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [route, locale]);

  /* ===========================
     RENDER
     =========================== */
  return (
    <div className="app-shell">
 <GlobalSEO />
      <Header
        locale={locale}
        onLocaleChange={setLocale}
        activeSection={activeSection}
        isBlogPage={route === 'blog' || route === 'article'}
      />

      <main id="main">
        {route === 'home' && (
          <>
            <Hero locale={locale} />
            <Specialisations locale={locale} />
            <Process locale={locale} />
            <BlogSection locale={locale} />
            <ContactSection locale={locale} />
          </>
        )}

        {route === 'about' && (
          <>
            <AboutProfessional locale={locale} />
            <WhyMe locale={locale} />
            <ForWhom locale={locale} />
          </>
        )}

        {route === 'blog' && <BlogPage locale={locale} />}

          {route === 'zwierzetaCywilHub' && <ZwierzetaCywilHub locale={locale} />}

        {route === 'zwierzetaCywilLanding' && (
          <ZwierzetaCywilLanding locale={locale} slug={zwierzetaCywilSlug} />
        )}
{route === 'sporyBankiHub' && <SporyZBankamiHub locale={locale} />}
{route === 'sporyBankiLanding' && sporyBankiSlug && (
  <SporyBankiLanding locale={locale} slug={sporyBankiSlug} />
)}
        {route === 'article' && (
          <BlogArticlePage locale={locale} slug={articleSlug || 'paragraf-zwierzoochronny'} />
        )}

      </main>

      <Footer
  locale={locale}
  onOpenPrivacy={() => setPrivacyOpen(true)}
  onOpenCookieSettings={() => window.dispatchEvent(new Event('cookies:settings'))}
/>

      {/* MODAL – POLITYKA PRYWATNOŚCI */}
      {privacyOpen && (
        <div
          className="legal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Polityka prywatności"
          onClick={() => setPrivacyOpen(false)}
        >
          <div className="legal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="legal-modal__header">
              <h2 className="legal-modal__title">
                {locale === 'pl'
                  ? 'Polityka prywatności i cookies'
                  : locale === 'en'
                  ? 'Privacy policy and cookies'
                  : 'Política de privacidad y cookies'}
              </h2>
              <button
                className="legal-modal__close"
                onClick={() => setPrivacyOpen(false)}
                aria-label="Zamknij"
              >
                ✕
              </button>
            </div>

          <div className="legal-modal__body">
  {locale === 'pl' && (
    <>
      <h3>1. Administrator danych osobowych</h3>
      <p>Administratorem danych osobowych jest:</p>
      <p>
        <strong>Pieńkowski – Kancelaria Adwokacka</strong>
        <br />
        ul. Bracka 20/7a, 00-028 Warszawa
        <br />
        e-mail: biuro@pienkowskilegal.pl
        <br />
        tel.: +48 889 054 555
        <br />
        NIP: 7162754247
      </p>
      <p>
        Administrator przetwarza dane osobowe użytkowników strony internetowej prowadzonej pod adresem
        pienkowskilegal.pl zgodnie z Rozporządzeniem Parlamentu Europejskiego i Rady Unii Europejskiej
        2016/679 z dnia 27 kwietnia 2016 roku w sprawie ochrony osób fizycznych w związku z przetwarzaniem
        danych osobowych i w sprawie swobodnego przepływu takich danych oraz uchylenia dyrektywy 95/46/WE
        (dalej: „RODO”).
      </p>
      <p>
        W sprawach związanych z ochroną danych osobowych należy skontaktować się z Administratorem za
        pośrednictwem poczty elektronicznej lub listownie na wskazane wyżej adresy.
      </p>

      <h3>2. Zakres przetwarzanych danych</h3>
      <p>Administrator może przetwarzać następujące dane osobowe:</p>
      <ul>
        <li>imię i nazwisko,</li>
        <li>adres e-mail,</li>
        <li>numer telefonu,</li>
        <li>treść wiadomości przesłanej za pomocą formularza kontaktowego lub poczty e-mail,</li>
        <li>
          adres IP oraz dane techniczne związane z korzystaniem ze strony, takie jak typ przeglądarki, rodzaj
          urządzenia, odwiedzone podstrony oraz zdarzenia na stronie, np. kliknięcie w numer telefonu, adres
          e-mail, WhatsApp, przycisk kontaktowy lub przesłanie formularza.
        </li>
      </ul>
      <p>
        Podanie danych w formularzu kontaktowym jest dobrowolne, lecz niezbędne do udzielenia odpowiedzi na
        zapytanie lub nawiązania kontaktu.
      </p>

      <h3>3. Cele i podstawy prawne przetwarzania danych</h3>
      <p>Dane osobowe są przetwarzane w następujących celach:</p>
      <ul>
        <li>
          Udzielenie odpowiedzi na zapytanie oraz prowadzenie korespondencji
          <br />
          <strong>Podstawa prawna:</strong>
          <ul>
            <li>art. 6 ust. 1 lit. b RODO – działania na żądanie osoby, której dane dotyczą,</li>
            <li>
              art. 6 ust. 1 lit. f RODO – prawnie uzasadniony interes Administratora polegający na prowadzeniu
              korespondencji.
            </li>
          </ul>
        </li>
        <li>
          Zapewnienie prawidłowego funkcjonowania strony internetowej oraz jej bezpieczeństwa
          <br />
          <strong>Podstawa prawna:</strong>
          <ul>
            <li>art. 6 ust. 1 lit. f RODO – prawnie uzasadniony interes Administratora.</li>
          </ul>
        </li>
        <li>
          Analiza statystyk odwiedzin strony, pomiar skuteczności działań marketingowych oraz pomiar konwersji
          <br />
          <strong>Podstawa prawna:</strong>
          <ul>
            <li>
              art. 6 ust. 1 lit. f RODO – prawnie uzasadniony interes Administratora polegający na analizie
              sposobu korzystania ze strony i ocenie skuteczności działań marketingowych,
            </li>
            <li>
              art. 6 ust. 1 lit. a RODO – zgoda użytkownika, jeżeli korzystanie z określonych plików cookies
              lub podobnych technologii wymaga zgody.
            </li>
          </ul>
        </li>
        <li>
          Wypełnienie obowiązków prawnych związanych z wykonywaniem zawodu adwokata
          <br />
          <strong>Podstawa prawna:</strong>
          <ul>
            <li>art. 6 ust. 1 lit. c RODO – obowiązek prawny ciążący na Administratorze.</li>
          </ul>
        </li>
      </ul>

      <h3>4. Odbiorcy danych</h3>
      <p>Dane osobowe mogą być przekazywane:</p>
      <ul>
        <li>podmiotom świadczącym na rzecz Administratora usługi informatyczne lub hostingowe,</li>
        <li>
          dostawcom narzędzi analitycznych i marketingowych, w szczególności Google LLC, w związku z
          korzystaniem z Google Analytics, Google Tag Manager oraz Google Ads,
        </li>
        <li>
          podmiotom świadczącym na rzecz Administratora usługi księgowe lub doradcze – wyłącznie w zakresie
          niezbędnym do prawidłowego świadczenia ww. usług,
        </li>
        <li>
          sądom, organom administracji publicznej oraz innym uczestnikom postępowań – jeżeli jest to konieczne
          w związku ze świadczeniem pomocy prawnej,
        </li>
        <li>oraz w przypadkach wynikających z przepisów prawa powszechnie obowiązującego.</li>
      </ul>
      <p>
        Dane mogą być przekazywane do państw trzecich poza Europejski Obszar Gospodarczy, na zasadach
        określonych w obowiązujących przepisach prawa, w szczególności przy zastosowaniu standardowych klauzul
        umownych lub innych mechanizmów przewidzianych przez RODO lub w związku z korzystaniem z narzędzi
        dostarczanych przez Google LLC.
      </p>

      <h3>5. Okres przechowywania danych</h3>
      <ul>
        <li>
          dane przekazane w związku z kontaktem – przez czas niezbędny do obsługi zapytania, a następnie przez
          okres przedawnienia ewentualnych roszczeń,
        </li>
        <li>
          dane przetwarzane na podstawie obowiązków prawnych – przez okres wymagany przepisami prawa
          powszechnie obowiązującego,
        </li>
        <li>
          dane związane ze statystykami, cookies i pomiarem konwersji – przez okres wynikający z ustawień danego
          narzędzia albo do czasu cofnięcia zgody, jeżeli przetwarzanie odbywa się na podstawie zgody.
        </li>
      </ul>

      <h3>6. Prawa osób, których dane dotyczą</h3>
      <p>Każdej osobie, której dane są przetwarzane, przysługuje prawo do:</p>
      <ul>
        <li>dostępu do danych oraz otrzymania ich kopii,</li>
        <li>sprostowania danych,</li>
        <li>usunięcia danych (w przypadkach przewidzianych prawem),</li>
        <li>ograniczenia przetwarzania danych,</li>
        <li>wniesienia sprzeciwu wobec przetwarzania danych,</li>
        <li>przenoszenia danych (jeżeli podstawą przetwarzania jest zgoda lub umowa),</li>
        <li>cofnięcia zgody w dowolnym momencie, jeżeli przetwarzanie odbywa się na podstawie zgody,</li>
        <li>wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.</li>
      </ul>

      <h3>7. Pliki cookies</h3>
      <p>Strona internetowa wykorzystuje pliki cookies lub podobne rozwiązania.</p>
      <p>
        Strona wykorzystuje pliki cookies o charakterze technicznym, niezbędne do jej prawidłowego działania, w
        tym do zapamiętania wybranych ustawień użytkownika.
      </p>
      <p>
        Strona może również korzystać z plików cookies o charakterze analitycznym, związanych w szczególności z
        narzędziem Google Analytics, w celu analizy statystyk odwiedzin i poprawy funkcjonowania serwisu.
      </p>
      <p>
        Strona może korzystać także z plików cookies i podobnych rozwiązań o charakterze marketingowym,
        związanych w szczególności z Google Ads, w celu pomiaru skuteczności reklam, pomiaru konwersji oraz
        prowadzenia działań marketingowych.
      </p>
      <p>
        Jeżeli korzystanie z określonych plików cookies lub podobnych technologii wymaga zgody użytkownika, są
        one stosowane po jej udzieleniu. Użytkownik może zmienić swoje ustawienia dotyczące plików cookies w
        ustawieniach przeglądarki internetowej lub za pomocą mechanizmu zarządzania zgodami dostępnego na stronie
        (w tym baner cookies).
      </p>

      <h3>8. Zautomatyzowane podejmowanie decyzji</h3>
      <p>
        Administrator nie podejmuje wobec użytkowników decyzji w sposób zautomatyzowany, które wywoływałyby
        wobec nich skutki prawne lub w inny sposób istotnie na nich wpływały.
      </p>
      <p>
        W związku z korzystaniem z narzędzi analitycznych i marketingowych Google LLC może dochodzić do
        profilowania użytkowników na zasadach określonych przez dostawcę tych narzędzi, w szczególności w celu
        analizy statystyk, pomiaru skuteczności reklam lub dopasowania treści reklamowych.
      </p>

      <h3>9. Zmiany Polityki prywatności</h3>
      <p>
        Administrator zastrzega sobie prawo do wprowadzania zmian w niniejszej Polityce prywatności w przypadku
        zmian przepisów prawa, technologii lub sposobu funkcjonowania strony. Aktualna wersja Polityki jest zawsze
        dostępna na stronie internetowej pienkowskilegal.pl.
      </p>
    </>
  )}

  {locale === 'en' && (
    <>
      <h3>1. Personal data controller</h3>
      <p>The controller of personal data is:</p>
      <p>
        <strong>Pieńkowski – Law Firm</strong>
        <br />
        ul. Bracka 20/7a, 00-028 Warsaw, Poland
        <br />
        e-mail: biuro@pienkowskilegal.pl
        <br />
        tel.: +48 889 054 555
        <br />
        Tax identification number: 7162754247
      </p>
      <p>
        The Controller processes personal data of users of the website available at pienkowskilegal.pl in
        accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016
        on the protection of natural persons with regard to the processing of personal data and on the free
        movement of such data, and repealing Directive 95/46/EC (“GDPR”).
      </p>
      <p>
        In matters related to personal data protection, you may contact the Controller by e-mail or by post using
        the addresses indicated above.
      </p>

      <h3>2. Scope of data processed</h3>
      <p>The Controller may process the following personal data:</p>
      <ul>
        <li>first name and surname,</li>
        <li>e-mail address,</li>
        <li>telephone number,</li>
        <li>content of a message sent through the contact form or by e-mail,</li>
        <li>
          IP address and technical data related to the use of the website, such as browser type, device type,
          visited subpages and events on the website, for example clicking a telephone number, e-mail address,
          WhatsApp, contact button or submitting the form.
        </li>
      </ul>
      <p>
        Providing data in the contact form is voluntary, but necessary to answer an enquiry or establish contact.
      </p>

      <h3>3. Purposes and legal bases of processing</h3>
      <p>Personal data are processed for the following purposes:</p>
      <ul>
        <li>
          Responding to enquiries and conducting correspondence
          <br />
          <strong>Legal basis:</strong>
          <ul>
            <li>Article 6(1)(b) GDPR – taking steps at the request of the data subject,</li>
            <li>
              Article 6(1)(f) GDPR – the legitimate interest of the Controller consisting in conducting
              correspondence.
            </li>
          </ul>
        </li>
        <li>
          Ensuring the proper functioning and security of the website
          <br />
          <strong>Legal basis:</strong>
          <ul>
            <li>Article 6(1)(f) GDPR – the legitimate interest of the Controller.</li>
          </ul>
        </li>
        <li>
          Analysing website traffic statistics, measuring the effectiveness of marketing activities and measuring
          conversions
          <br />
          <strong>Legal basis:</strong>
          <ul>
            <li>
              Article 6(1)(f) GDPR – the legitimate interest of the Controller consisting in analysing the use of
              the website and assessing the effectiveness of marketing activities,
            </li>
            <li>
              Article 6(1)(a) GDPR – the user’s consent, where the use of certain cookies or similar technologies
              requires consent.
            </li>
          </ul>
        </li>
        <li>
          Fulfilling legal obligations related to the practice of the legal profession
          <br />
          <strong>Legal basis:</strong>
          <ul>
            <li>Article 6(1)(c) GDPR – compliance with a legal obligation to which the Controller is subject.</li>
          </ul>
        </li>
      </ul>

      <h3>4. Data recipients</h3>
      <p>Personal data may be disclosed to:</p>
      <ul>
        <li>IT or hosting service providers acting on behalf of the Controller,</li>
        <li>
          providers of analytical and marketing tools, in particular Google LLC, in connection with the use of
          Google Analytics, Google Tag Manager and Google Ads,
        </li>
        <li>
          accounting or advisory service providers – only to the extent necessary to provide such services
          properly,
        </li>
        <li>
          courts, public authorities and other participants in proceedings – where necessary in connection with
          the provision of legal assistance,
        </li>
        <li>and in other cases resulting from generally applicable laws.</li>
      </ul>
      <p>
        Data may be transferred to countries outside the European Economic Area in accordance with applicable
        laws, in particular on the basis of standard contractual clauses or other mechanisms provided for by the
        GDPR, or in connection with the use of tools provided by Google LLC.
      </p>

      <h3>5. Data retention period</h3>
      <ul>
        <li>
          data provided in connection with contact – for the time necessary to handle the enquiry, and then for
          the limitation period of potential claims,
        </li>
        <li>
          data processed to comply with legal obligations – for the period required by generally applicable law,
        </li>
        <li>
          data related to statistics, cookies and conversion measurement – for the period resulting from the
          settings of a given tool or until consent is withdrawn, if the processing is based on consent.
        </li>
      </ul>

      <h3>6. Rights of data subjects</h3>
      <p>Each person whose data are processed has the right to:</p>
      <ul>
        <li>access their data and receive a copy,</li>
        <li>rectify data,</li>
        <li>erase data (in cases provided by law),</li>
        <li>restrict processing,</li>
        <li>object to processing,</li>
        <li>data portability, where processing is based on consent or a contract,</li>
        <li>withdraw consent at any time, where processing is based on consent,</li>
        <li>lodge a complaint with the competent data protection authority.</li>
      </ul>

      <h3>7. Cookies</h3>
      <p>The website uses cookies or similar solutions.</p>
      <p>
        The website uses technical cookies necessary for its proper functioning, including remembering selected
        user settings.
      </p>
      <p>
        The website may also use analytical cookies, in particular those related to Google Analytics, in order to
        analyse website traffic statistics and improve the functioning of the website.
      </p>
      <p>
        The website may also use marketing cookies and similar solutions, in particular those related to Google
        Ads, in order to measure the effectiveness of advertisements, measure conversions and conduct marketing
        activities.
      </p>
      <p>
        If the use of certain cookies or similar technologies requires the user’s consent, they are used after
        such consent has been given. The user may change cookie settings in the web browser settings or by using
        the consent management mechanism available on the website, including the cookie banner.
      </p>

      <h3>8. Automated decision-making</h3>
      <p>
        The Controller does not make decisions concerning users in an automated manner that would produce legal
        effects concerning them or similarly significantly affect them.
      </p>
      <p>
        In connection with the use of analytical and marketing tools provided by Google LLC, users may be profiled
        under the rules set by the provider of those tools, in particular for the purpose of analysing statistics,
        measuring the effectiveness of advertisements or matching advertising content.
      </p>

      <h3>9. Changes to this policy</h3>
      <p>
        The Controller reserves the right to amend this Privacy Policy in the event of changes in law, technology
        or the way the website operates. The current version of the policy is always available on
        pienkowskilegal.pl.
      </p>
    </>
  )}

  {locale === 'es' && (
    <>
      <h3>1. Responsable del tratamiento de datos personales</h3>
      <p>El responsable del tratamiento de los datos personales es:</p>
      <p>
        <strong>Pieńkowski – Despacho de Abogados</strong>
        <br />
        ul. Bracka 20/7a, 00-028 Varsovia, Polonia
        <br />
        e-mail: biuro@pienkowskilegal.pl
        <br />
        tel.: +48 889 054 555
        <br />
        NIP: 7162754247
      </p>
      <p>
        El Responsable trata los datos personales de los usuarios del sitio web disponible en pienkowskilegal.pl
        de conformidad con el Reglamento (UE) 2016/679 del Parlamento Europeo y del Consejo, de 27 de abril de
        2016, relativo a la protección de las personas físicas en lo que respecta al tratamiento de datos
        personales y a la libre circulación de estos datos, y por el que se deroga la Directiva 95/46/CE
        (“RGPD”).
      </p>
      <p>
        Para asuntos relacionados con la protección de datos personales, puede ponerse en contacto con el
        Responsable por correo electrónico o por correo postal utilizando las direcciones indicadas arriba.
      </p>

      <h3>2. Alcance de los datos tratados</h3>
      <p>El Responsable puede tratar los siguientes datos personales:</p>
      <ul>
        <li>nombre y apellidos,</li>
        <li>dirección de correo electrónico,</li>
        <li>número de teléfono,</li>
        <li>contenido del mensaje enviado mediante el formulario de contacto o por correo electrónico,</li>
        <li>
          dirección IP y datos técnicos relacionados con el uso del sitio web, como el tipo de navegador, el tipo
          de dispositivo, las subpáginas visitadas y los eventos en el sitio web, por ejemplo hacer clic en el
          número de teléfono, la dirección de correo electrónico, WhatsApp, el botón de contacto o enviar el
          formulario.
        </li>
      </ul>
      <p>
        La aportación de datos en el formulario de contacto es voluntaria, pero necesaria para responder a una
        consulta o establecer contacto.
      </p>

      <h3>3. Finalidades y bases jurídicas del tratamiento</h3>
      <p>Los datos personales se tratan para las siguientes finalidades:</p>
      <ul>
        <li>
          Responder a consultas y mantener correspondencia
          <br />
          <strong>Base jurídica:</strong>
          <ul>
            <li>art. 6.1(b) del RGPD – medidas adoptadas a petición del interesado,</li>
            <li>
              art. 6.1(f) del RGPD – interés legítimo del Responsable consistente en mantener correspondencia.
            </li>
          </ul>
        </li>
        <li>
          Garantizar el correcto funcionamiento y la seguridad del sitio web
          <br />
          <strong>Base jurídica:</strong>
          <ul>
            <li>art. 6.1(f) del RGPD – interés legítimo del Responsable.</li>
          </ul>
        </li>
        <li>
          Analizar las estadísticas de visitas al sitio web, medir la eficacia de las actividades de marketing y
          medir las conversiones
          <br />
          <strong>Base jurídica:</strong>
          <ul>
            <li>
              art. 6.1(f) del RGPD – interés legítimo del Responsable consistente en analizar el uso del sitio web
              y evaluar la eficacia de las actividades de marketing,
            </li>
            <li>
              art. 6.1(a) del RGPD – consentimiento del usuario, cuando el uso de determinadas cookies o
              tecnologías similares requiera consentimiento.
            </li>
          </ul>
        </li>
        <li>
          Cumplir las obligaciones legales relacionadas con el ejercicio de la profesión de abogado
          <br />
          <strong>Base jurídica:</strong>
          <ul>
            <li>art. 6.1(c) del RGPD – cumplimiento de una obligación legal aplicable al Responsable.</li>
          </ul>
        </li>
      </ul>

      <h3>4. Destinatarios de los datos</h3>
      <p>Los datos personales podrán ser comunicados a:</p>
      <ul>
        <li>proveedores de servicios informáticos o de alojamiento que presten servicios al Responsable,</li>
        <li>
          proveedores de herramientas analíticas y de marketing, en particular Google LLC, en relación con el uso
          de Google Analytics, Google Tag Manager y Google Ads,
        </li>
        <li>
          proveedores de servicios contables o de asesoría – únicamente en la medida necesaria para la correcta
          prestación de dichos servicios,
        </li>
        <li>
          tribunales, autoridades públicas y otros participantes en procedimientos – cuando sea necesario en
          relación con la prestación de asistencia jurídica,
        </li>
        <li>y en otros casos derivados de la normativa aplicable.</li>
      </ul>
      <p>
        Los datos podrán transferirse a países fuera del Espacio Económico Europeo de conformidad con la normativa
        aplicable, en particular mediante cláusulas contractuales tipo u otros mecanismos previstos por el RGPD, o
        en relación con el uso de herramientas proporcionadas por Google LLC.
      </p>

      <h3>5. Plazo de conservación de los datos</h3>
      <ul>
        <li>
          datos proporcionados en relación con el contacto – durante el tiempo necesario para gestionar la
          consulta y, posteriormente, durante el plazo de prescripción de posibles reclamaciones,
        </li>
        <li>
          datos tratados para cumplir obligaciones legales – durante el período exigido por la normativa de
          aplicación general,
        </li>
        <li>
          datos relacionados con estadísticas, cookies y medición de conversiones – durante el período resultante
          de la configuración de la herramienta correspondiente o hasta la retirada del consentimiento, si el
          tratamiento se basa en el consentimiento.
        </li>
      </ul>

      <h3>6. Derechos de las personas interesadas</h3>
      <p>Toda persona cuyos datos se tratan tiene derecho a:</p>
      <ul>
        <li>acceder a sus datos y obtener una copia,</li>
        <li>rectificar los datos,</li>
        <li>suprimir los datos en los casos previstos por la ley,</li>
        <li>limitar el tratamiento,</li>
        <li>oponerse al tratamiento,</li>
        <li>portabilidad de los datos, cuando el tratamiento se base en el consentimiento o en un contrato,</li>
        <li>retirar el consentimiento en cualquier momento, cuando el tratamiento se base en el consentimiento,</li>
        <li>presentar una reclamación ante la autoridad de protección de datos competente.</li>
      </ul>

      <h3>7. Cookies</h3>
      <p>El sitio web utiliza cookies o soluciones similares.</p>
      <p>
        El sitio web utiliza cookies técnicas necesarias para su correcto funcionamiento, incluido el
        almacenamiento de determinados ajustes del usuario.
      </p>
      <p>
        El sitio web también puede utilizar cookies analíticas, en particular relacionadas con Google Analytics,
        con el fin de analizar las estadísticas de visitas y mejorar el funcionamiento del sitio.
      </p>
      <p>
        El sitio web también puede utilizar cookies de marketing y soluciones similares, en particular
        relacionadas con Google Ads, con el fin de medir la eficacia de los anuncios, medir conversiones y llevar
        a cabo actividades de marketing.
      </p>
      <p>
        Si el uso de determinadas cookies o tecnologías similares requiere el consentimiento del usuario, estas se
        utilizan después de que dicho consentimiento haya sido otorgado. El usuario puede modificar la
        configuración de cookies en la configuración de su navegador o mediante el mecanismo de gestión de
        consentimientos disponible en el sitio web, incluido el banner de cookies.
      </p>

      <h3>8. Decisiones automatizadas</h3>
      <p>
        El Responsable no adopta decisiones respecto de los usuarios de forma automatizada que produzcan efectos
        jurídicos sobre ellos o les afecten significativamente de modo similar.
      </p>
      <p>
        En relación con el uso de herramientas analíticas y de marketing de Google LLC, puede producirse la
        elaboración de perfiles de los usuarios conforme a las normas establecidas por el proveedor de dichas
        herramientas, en particular para analizar estadísticas, medir la eficacia de los anuncios o adaptar
        contenidos publicitarios.
      </p>

      <h3>9. Cambios en la política</h3>
      <p>
        El Responsable se reserva el derecho de introducir cambios en esta Política de privacidad en caso de
        cambios en la legislación, la tecnología o el funcionamiento del sitio web. La versión vigente de la
        política está siempre disponible en pienkowskilegal.pl.
      </p>
    </>
  )}
</div>

          </div>
        </div>
      )}
<CookieConsentBanner
  locale={locale}
  onChangeLocale={setLocale}
  onOpenPrivacy={() => setPrivacyOpen(true)}
/>
    </div>
  );
}

export default App;
